import * as z from "zod";
import {
  HsaConditionExpression,
  hsaConditionExpression,
} from "./hsa-condition";

// Flat-array flow representation. Storing nodes by id rather than as
// embedded children supports DAGs (paths converging on a shared downstream
// node) and makes cycle detection a runtime concern rather than a structural
// impossibility. The validator forbids cycles; the runner detects them as a
// safety net.
//
// Node types:
//   GROUP     — a "page" of questions rendered together. Holds an ordered
//               list of question handles that reference the pool. Single
//               outgoing edge (`nextId`).
//   CONDITION — a branching node. Holds a condition expression and routes
//               to one of two edges based on prior answers.
//
// Questions never appear as standalone nodes. Their content lives in the
// pool (hsa-question-pool.ts); groups reference them by handle.

export enum HsaFlowNodeType {
  GROUP = "GROUP",
  CONDITION = "CONDITION",
  KNOCKOUT = "KNOCKOUT",
  CONTENT = "CONTENT",
}

// Branded UUID type for every node identity in the flow graph. The brand
// stops a plain `string` from being assignable where an `HsaNodeId` is
// expected, so callers can't accidentally pass a handle or some other string.
// Generation always happens server-side via `crypto.randomUUID()`; the
// agent never types one.
export const hsaNodeIdSchema = z.uuid().brand<"HsaNodeId">();
export type HsaNodeId = z.infer<typeof hsaNodeIdSchema>;

// Helper for tests and in-memory fixture builders. Production code should
// never have a raw string id — the schema parses everything off the wire.
export function hsaNodeId(value: string): HsaNodeId {
  return hsaNodeIdSchema.parse(value);
}

const hsaGroupNodeSchema_ = z.object({
  nodeId: hsaNodeIdSchema,
  nodeType: z.literal(HsaFlowNodeType.GROUP),
  // Stable, kebab-case identifier for the group ("user-info",
  // "medical-info-diagnosis"). Used in analytics + condition refs to
  // distinguish answers across groups when needed. Must be unique within a
  // flow.
  groupHandle: z.string().min(1),
  // Optional human-friendly title for analytics + admin tooling. The UI
  // does not currently render this — questions carry their own labels.
  title: z.string().optional(),
  // Pool handle references in render order. Validator enforces every handle
  // exists in the pool and no handle is duplicated within the flow.
  questionHandles: z.array(z.string().min(1)).min(1),
  nextId: hsaNodeIdSchema.nullable(),
});
export const hsaGroupNodeSchema: z.ZodType<HsaGroupNode> = hsaGroupNodeSchema_;
export interface HsaGroupNode {
  nodeId: HsaNodeId;
  nodeType: HsaFlowNodeType.GROUP;
  groupHandle: string;
  title?: string;
  questionHandles: string[];
  nextId: HsaNodeId | null;
}

const hsaConditionNodeSchema_ = z.object({
  nodeId: hsaNodeIdSchema,
  nodeType: z.literal(HsaFlowNodeType.CONDITION),
  condition: hsaConditionExpression,
  matchingNextId: hsaNodeIdSchema.nullable(),
  nonMatchingNextId: hsaNodeIdSchema.nullable(),
});
export const hsaConditionNodeSchema: z.ZodType<HsaConditionNode> =
  hsaConditionNodeSchema_;
export interface HsaConditionNode {
  nodeId: HsaNodeId;
  nodeType: HsaFlowNodeType.CONDITION;
  condition: HsaConditionExpression;
  matchingNextId: HsaNodeId | null;
  nonMatchingNextId: HsaNodeId | null;
}

// A KNOCKOUT node is a terminal that ends the walk with a disqualified
// verdict. `reason` is machine-readable, for failure routing + analytics.
const hsaKnockoutNodeSchema_ = z.object({
  nodeId: hsaNodeIdSchema,
  nodeType: z.literal(HsaFlowNodeType.KNOCKOUT),
  reason: z.string().min(1),
});
export const hsaKnockoutNodeSchema: z.ZodType<HsaKnockoutNode> =
  hsaKnockoutNodeSchema_;
export interface HsaKnockoutNode {
  nodeId: HsaNodeId;
  nodeType: HsaFlowNodeType.KNOCKOUT;
  reason: string;
}

// A CONTENT node renders an informational screen (welcome, interstitial) with a
// Continue button and no question input; `recordsConsent` marks the clickwrap.
const hsaContentNodeSchema_ = z.object({
  nodeId: hsaNodeIdSchema,
  nodeType: z.literal(HsaFlowNodeType.CONTENT),
  contentHandle: z.string().min(1),
  heading: z.string().min(1),
  body: z.array(z.string()),
  recordsConsent: z.boolean(),
  nextId: hsaNodeIdSchema.nullable(),
});
export const hsaContentNodeSchema: z.ZodType<HsaContentNode> =
  hsaContentNodeSchema_;
export interface HsaContentNode {
  nodeId: HsaNodeId;
  nodeType: HsaFlowNodeType.CONTENT;
  contentHandle: string;
  heading: string;
  body: string[];
  recordsConsent: boolean;
  nextId: HsaNodeId | null;
}

const hsaFlowNodeSchema_ = z.discriminatedUnion("nodeType", [
  hsaGroupNodeSchema_,
  hsaConditionNodeSchema_,
  hsaKnockoutNodeSchema_,
  hsaContentNodeSchema_,
]);
export const hsaFlowNodeSchema: z.ZodType<HsaFlowNode> = hsaFlowNodeSchema_;
export type HsaFlowNode =
  | HsaGroupNode
  | HsaConditionNode
  | HsaKnockoutNode
  | HsaContentNode;

// `rootId` may be null only for an empty flow (no nodes). When non-null
// the validator enforces it points to a group node — flows must start on
// a group, never a condition (conditions reference prior answers and have
// nothing to evaluate as the first step).
const hsaFlowSchema_ = z.object({
  rootId: hsaNodeIdSchema.nullable(),
  nodes: z.array(hsaFlowNodeSchema_),
});
export const hsaFlow: z.ZodType<HsaFlow> = hsaFlowSchema_;
export interface HsaFlow {
  rootId: HsaNodeId | null;
  nodes: HsaFlowNode[];
}
